/**
 * Which paywall, if any, a refused request is asking for.
 *
 * The server's payment gate answers **402** with `detail.error` naming the
 * refusal. 402 alone is not enough — `quota_exceeded` is also a 402, and it
 * means "talk to your administrator", not "pay here" — so every surface that
 * can be refused (audit a section, build the audit library, draft more on a
 * custom run) branches on this one reading rather than on the status.
 *
 *   * `payment_required_custom` — a document opened as a custom submission is
 *     not unlocked; the flat unlock (`UnlockAsthraDialog`) covers building on it
 *     and auditing every section.
 *   * `payment_required` — a drafted document's section is not paid for.
 */

export type PaywallKind = "custom" | "section";

export function paywallOf(err: unknown): PaywallKind | null {
  if (!(err instanceof Error)) return null;
  const { status, detail } = err as Error & { status?: number; detail?: unknown };
  if (status !== 402 || !detail || typeof detail !== "object") return null;
  const code = (detail as { error?: unknown }).error;
  if (code === "payment_required_custom") return "custom";
  if (code === "payment_required") return "section";
  return null;
}

/**
 * A refused action asking the shell to put a paywall up.
 *
 * Refusals surface deep in the tree — the review store, the issues panels, the
 * draft flow — while the dialogs and checkout live in the shell. Rather than
 * thread a callback through each, they post here and the shell (the one
 * subscriber) decides. `retry` re-runs the refused action once the document is
 * unlocked.
 */
export interface PaywallRequest {
  readonly kind: PaywallKind;
  /** The run's backend doctype, when the caller knows it. */
  readonly docType?: string | null;
  readonly retry?: () => void;
}

type PaywallListener = (request: PaywallRequest) => boolean;

let paywallListener: PaywallListener | null = null;

/** Subscribe the shell. Returns the unsubscribe. */
export function onPaywallRequest(listener: PaywallListener): () => void {
  paywallListener = listener;
  return () => {
    if (paywallListener === listener) paywallListener = null;
  };
}

/** Ask for a paywall. `true` when one was put up; `false` leaves the caller to report the error. */
export function requestPaywall(request: PaywallRequest): boolean {
  return paywallListener?.(request) ?? false;
}

/**
 * The error text for a refusal that put a paywall up (so the panel does not
 * also show the raw 402), or the error's own message otherwise.
 */
export function refusalMessage(err: unknown, retry?: () => void, docType?: string | null): string {
  const kind = paywallOf(err);
  if (kind === "custom" && requestPaywall({ kind, docType, retry })) {
    return "Unlock this document to build on it and run a regulatory review on it.";
  }
  if (kind === "section") {
    return "This section isn't paid for yet — draft it first, then run the review.";
  }
  return err instanceof Error ? err.message : String(err);
}
